import { readBarcodesFromImageData, prepareZXingModule, type ReaderOptions } from 'zxing-wasm/reader';
import { ImagePixels, QrDecoder } from '../core/ports';
import { RawSymbol } from '../core/types';

/**
 * QR decoder backed by zxing-wasm (C++ ZXing compiled to WebAssembly).
 *
 * Chosen after benchmarking every JS/WASM option on real Brazilian-document
 * photos: it reads the dense CNH-e *binary* QR (exposing raw `.bytes`) AND the
 * camera photos that jsQR/zbar/OpenCV miss — with no preprocessing. Pure WASM,
 * so the same adapter runs in the browser and in a Node Lambda.
 */
export interface ZxingDecoderOptions {
  /** URL/path to the self-hosted `zxing_reader.wasm`. */
  wasmLocation?: string;
  /** Override ZXing reader options (defaults favour recall). */
  readerOptions?: ReaderOptions;
}

const DEFAULT_READER_OPTIONS: ReaderOptions = {
  formats: ['QRCode'],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  maxNumberOfSymbols: 4,
};

export class ZxingDecoder implements QrDecoder {
  private readonly options: ReaderOptions;

  constructor(opts: ZxingDecoderOptions = {}) {
    this.options = { ...DEFAULT_READER_OPTIONS, ...opts.readerOptions };
    if (opts.wasmLocation) {
      // Self-host the wasm instead of fetching it from the default CDN.
      prepareZXingModule({
        overrides: {
          locateFile: (path: string, prefix: string) =>
            path.endsWith('.wasm') ? opts.wasmLocation! : prefix + path,
        },
        fireImmediately: false,
      });
    }
  }

  async decode(image: ImagePixels): Promise<RawSymbol[]> {
    const results = await readBarcodesFromImageData(image as ImageData, this.options);
    return results
      .filter((r) => r.text || r.bytes?.length)
      .map((r) => ({
        bytes: r.bytes ?? new Uint8Array(),
        text: r.text ?? '',
        format: r.format,
      }));
  }
}
